import React from "react";

// AST nodes for inline formatting
type InlineNode =
  | { type: "text"; content: string }
  | { type: "bold"; content: InlineNode[] }
  | { type: "italic"; content: InlineNode[] }
  | { type: "underline"; content: InlineNode[] };

// Block nodes: paragraph or list
type BlockNode =
  | { type: "paragraph"; content: InlineNode[] }
  | { type: "list"; items: InlineNode[][] };

type FormattedTextProps = { text: string };

/** Parse inline formatting (**bold**, *italic*, ~~underline~~) recursively */
function parseInline(input: string): InlineNode[] {
  const elements: InlineNode[] = [];
  let remaining = input;

  const patterns: Array<{
    type: "bold" | "italic" | "underline";
    regex: RegExp;
  }> = [
    { type: "bold", regex: /\*\*([\s\S]+?)\*\*/ },
    { type: "bold", regex: /__([\s\S]+?)__/ },
    { type: "italic", regex: /\*([\s\S]+?)\*/ },
    { type: "italic", regex: /_([\s\S]+?)_/ },
    { type: "underline", regex: /~~([\s\S]+?)~~/ },
  ];

  while (remaining.length > 0) {
    let earliest: {
      type: "bold" | "italic" | "underline";
      m: RegExpExecArray;
      idx: number;
    } | null = null;

    for (const p of patterns) {
      const m = p.regex.exec(remaining);
      if (m) {
        const idx = m.index;
        if (!earliest || idx < earliest.idx) {
          earliest = { type: p.type, m, idx };
        }
      }
    }

    if (!earliest) {
      elements.push({ type: "text", content: remaining });
      break;
    }

    if (earliest.idx > 0) {
      elements.push({
        type: "text",
        content: remaining.slice(0, earliest.idx),
      });
    }

    const inner = earliest.m[1];
    elements.push({
      type: earliest.type,
      content: parseInline(inner),
    });

    remaining = remaining.slice(earliest.idx + earliest.m[0].length);
  }

  return elements;
}

/** Structure when a paragraph contains inline list markers */
type ListExtraction = { before: string; items: string[]; after: string };

/**
 * Extract list structure from a paragraph that may contain:
 *   ...telles que : \n--item 1,--item 2,--item 3
 * We treat `--` (or en/em dash) as a **start-of-item** marker and capture until the next marker or end.
 * Commas/whitespace around items are trimmed. Returns before/items/after so we don't drop text.
 */
function extractListStructure(text: string): ListExtraction {
  // Normalize literal \n to real newlines and allow en/em dash as markers
  const normalized = text.replace(/\\n/g, "\n").replace(/[–—]/g, "--");

  const marker = /--/g;
  const starts: number[] = [];
  let m: RegExpExecArray | null;

  while ((m = marker.exec(normalized)) !== null) {
    starts.push(m.index);
  }

  if (starts.length === 0) {
    return { before: normalized, items: [], after: "" };
  }

  // The paragraph can have text before the first item and after the last item.
  const before = normalized.slice(0, starts[0]).trimEnd();

  // Compute item slices between markers (or end of string for the last one)
  const items: string[] = [];
  for (let i = 0; i < starts.length; i++) {
    const start = starts[i] + 2; // skip the marker "--"
    const end = i + 1 < starts.length ? starts[i + 1] : normalized.length;
    const raw = normalized.slice(start, end);

    // If there's a trailing comma before the next marker, drop it; also trim spaces and common punctuation
    const cleaned = raw
      .replace(/^[\s,;:]+/, "")
      .replace(/[\s,;:.]+$/, "")
      .trim();

    if (cleaned) items.push(cleaned);
  }

  // After text = anything after the last item slice
  let after = "";
  const postMatch = normalized
    .slice(starts[starts.length - 1] + 2)
    .match(/[\s,;:.]*[\r\n]+([\s\S]+)$/);
  if (postMatch && postMatch[1].trim()) {
    after = postMatch[1].trimStart();
  }

  return { before, items, after };
}

/** Parse blocks: paragraphs split by blank lines; inside a paragraph, keep text + list if present */
function parseBlocks(raw: string): BlockNode[] {
  // Normalisation minimale
  const normalized = raw
    .replace(/\r\n/g, "\n")
    .replace(/\r/g, "\n")
    .replace(/\\n/g, "\n") // pour les "\n" littéraux
    .replace(/[–—]/g, "--"); // si tu veux garder la logique des tirets

  const blocks: BlockNode[] = [];

  // On laisse extractListStructure détecter les listes,
  // mais on ne touche pas aux sauts de ligne.
  const { before, items, after } = extractListStructure(normalized);

  if (items.length === 0) {
    // Aussi ici : tout est un paragraphe unique avec des \n à l'intérieur
    blocks.push({
      type: "paragraph",
      content: parseInline(normalized),
    });
  } else {
    if (before.trim()) {
      blocks.push({
        type: "paragraph",
        content: parseInline(before),
      });
    }

    blocks.push({
      type: "list",
      items: items.map((it) => parseInline(it)),
    });

    if (after.trim()) {
      blocks.push({
        type: "paragraph",
        content: parseInline(after),
      });
    }
  }

  return blocks;
}

const hasFormattingOrNewline = (text: string): boolean => {
  // Detects **, __, *, _, ~~ or -- (list marker) or any newline (real or literal "\\n")
  return /(\*\*|__|\*|_|~~|--|\n|\\n)/.test(text);
};

const EmtMarkdown: React.FC<FormattedTextProps> = ({ text }) => {
  if (!hasFormattingOrNewline(text)) {
    // No formatting or newline detected, render as plain paragraph
    return <>{text}</>;
  }

  const blocks = parseBlocks(text);

  const renderInline = (nodes: InlineNode[], keyPrefix = ""): React.ReactNode =>
    nodes.map((n, i) => {
      const key = `${keyPrefix}i${i}`;
      switch (n.type) {
        case "text": {
          const parts = n.content.split(/\n/);

          return parts.map((part, idx2) => (
            <React.Fragment key={`${key}-t-${idx2}`}>
              {part}
              {idx2 < parts.length - 1 && <br />}
            </React.Fragment>
          ));
        }

        case "bold":
          return (
            <strong key={key}>{renderInline(n.content, key + "-b-")}</strong>
          );
        case "italic":
          return <em key={key}>{renderInline(n.content, key + "-i-")}</em>;
        case "underline":
          return <u key={key}>{renderInline(n.content, key + "-u-")}</u>;
        default:
          return null;
      }
    });

  const renderBlocks = (nodes: BlockNode[]): React.ReactNode =>
    nodes.map((b, idx) => {
      if (b.type === "paragraph") {
        return (
          <p key={`p-${idx}`} style={{ margin: "0", whiteSpace: "pre-line" }}>
            {renderInline(b.content, `p-${idx}-`)}
          </p>
        );
      }
      return (
        <ul key={`ul-${idx}`} style={{ paddingLeft: 20, margin: "10px 0" }}>
          {b.items.map((item, liIdx) => (
            <li key={`li-${idx}-${liIdx}`}>
              {renderInline(item, `li-${idx}-${liIdx}-`)}
            </li>
          ))}
        </ul>
      );
    });

  return <>{renderBlocks(blocks)}</>;
};

export default EmtMarkdown;
